import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspace } from '../contexts/ContextoWorkspace';
import { cuentaContableService } from '../services/api/cuentaContables.service';
import type { AccountingAccount, AccountingAccountInput } from '../modelos/CuentaContable';
import type { DatoContable } from '../modelos/Configuracion';

interface UseAccountingAccountsReturn {
  accounts: AccountingAccount[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createAccount: (input: AccountingAccountInput) => Promise<AccountingAccount>;
  updateAccount: (id: string, input: AccountingAccountInput) => Promise<AccountingAccount>;
  deleteAccount: (id: string) => Promise<void>;
}

export function useAccountingAccounts(): UseAccountingAccountsReturn {
  const { empresaId } = useWorkspace();
  const [accounts, setAccounts] = useState<AccountingAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref para prevenir ejecuciones concurrentes
  const esCargandoRef = useRef(false);
  const hasLoadedRef = useRef(false);

  const scopeReady = empresaId != null && empresaId.trim() !== '';

  const refresh = useCallback(async () => {
    if (!scopeReady || !empresaId) {
      setAccounts([]);
      hasLoadedRef.current = false;
      return;
    }

    // Prevenir ejecuciones concurrentes
    if (esCargandoRef.current) return;
    esCargandoRef.current = true;

    setLoading(true);
    setError(null);
    try {
      const response = await cuentaContableService.getAll(empresaId);
      const mapped: AccountingAccount[] = (response.data || []).map((item: DatoContable) => ({
        id: String(item.id || ''),
        code: String(item.codigo || ''),
        createdAt: new Date(item.createdAt || Date.now()),
        updatedAt: new Date(item.updatedAt || Date.now()),
      }));
      setAccounts(mapped);
      hasLoadedRef.current = true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudieron cargar las cuentas contables';
      setError(message);
    } finally {
      setLoading(false);
      esCargandoRef.current = false;
    }
  }, [empresaId, scopeReady]);

  // useEffect solo se ejecuta cuando empresaId cambia (no cuando refresh se recrea)
  useEffect(() => {
    // Resetear flag cuando cambia empresaId
    hasLoadedRef.current = false;
    void refresh();
    
  }, [empresaId, scopeReady]);

  const createAccount = useCallback(async (input: AccountingAccountInput): Promise<AccountingAccount> => {
    if (!scopeReady || !empresaId) throw new Error('Selecciona una empresa para registrar cuentas contables');
    setLoading(true);
    setError(null);
    try {
      const payload = {
        empresaId,
        codigo: input.code,
      };
      const response = await cuentaContableService.create(payload);
      const created: AccountingAccount = {
        id: String(response.data.id),
        code: input.code,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setAccounts((prev) => [...prev, created]);
      return created;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo crear la cuenta contable';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [empresaId, scopeReady]);

  const updateAccount = useCallback(async (id: string, input: AccountingAccountInput): Promise<AccountingAccount> => {
    if (!scopeReady || !empresaId) throw new Error('Selecciona una empresa para actualizar cuentas contables');
    setLoading(true);
    setError(null);
    try {
      const payload = {
        empresaId,
        codigo: input.code,
      };
      await cuentaContableService.update(id, payload);
      const updated: AccountingAccount = {
        id,
        code: input.code,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setAccounts((prev) => prev.map((item) => (item.id === id ? updated : item)));
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo actualizar la cuenta contable';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [empresaId, scopeReady]);

  const deleteAccount = useCallback(async (id: string): Promise<void> => {
    if (!scopeReady) throw new Error('Selecciona una empresa para eliminar cuentas contables');
    setLoading(true);
    setError(null);
    try {
      await cuentaContableService.delete(id);
      setAccounts((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo eliminar la cuenta contable';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [scopeReady]);

  return {
    accounts,
    loading,
    error,
    refresh,
    createAccount,
    updateAccount,
    deleteAccount,
  };
}
